// Zweck: Dauerhafte, benutzergebundene Lagerwarteschlange.
// Kontext: Unklare Antworten behalten ihre ID; keine Upload-Queue-Abhängigkeit. Stand: 20.10.2026.
import localforage from 'localforage';
import type {
  WarehouseLocal,
  WarehouseTransport,
  WarehouseSnapshot,
} from '../interfaces/warehouse';

export const emptyWarehouse = (): WarehouseLocal => ({
  snapshot: null,
  draft: null,
  queue: [],
});
// Die Kontentrennung erfolgt ausschließlich über warehouseKey, auch beim Lesen und Löschen.
export const warehouseStore = () =>
  localforage.createInstance({
    name: '360volt-warehouse',
    storeName: 'warehouse',
    description: 'Kontogebundene Lagerentwürfe und Warteschlangen',
  });
export const warehouseKey = (owner: string, mode: string) =>
  `${location.origin}:${mode}:${owner.toLowerCase()}`;
export function warehouseAccessStatus(error: unknown): 401 | 403 | undefined {
  const status = (error as { status?: unknown } | null)?.status;
  return status === 401 || status === 403 ? status : undefined;
}
export const confirmedWarehouseRequests = (state: WarehouseLocal) =>
  new Set(
    state.queue
      .filter((q) => q.state === 'confirmed')
      .map((q) => q.booking.requestId),
  );
export function withWarehouseSnapshot(
  state: WarehouseLocal,
  snapshot: WarehouseSnapshot,
  confirmedBeforeRead: Set<string> = new Set(),
): WarehouseLocal {
  const results = new Map(snapshot.bookings.map((b) => [b.requestId, b]));
  return {
    ...state,
    snapshot,
    queue: state.queue.map((entry) => {
      const result = results.get(entry.booking.requestId);
      const updated =
        result && ['pending', 'unknown'].includes(entry.state)
          ? {
              ...entry,
              state: result.status,
              result,
              error: result.message ?? undefined,
            }
          : entry;
      return result || confirmedBeforeRead.has(entry.booking.requestId)
        ? { ...updated, stockIncluded: true }
        : updated;
    }),
  };
}
export function warehouseStock(
  state: WarehouseLocal,
  articleId: string,
  locationId: string,
): number {
  let quantity =
    state.snapshot?.stocks.find(
      (x) => x.articleId === articleId && x.locationId === locationId,
    )?.quantity ?? 0;
  for (const q of state.queue) {
    if (
      ['rejected', 'cancelled', 'superseded'].includes(q.state) ||
      (q.state === 'confirmed' && q.stockIncluded === true) ||
      q.booking.locationId !== locationId ||
      state.snapshot?.bookings.some(
        (x) => x.requestId === q.booking.requestId && x.status === 'confirmed',
      )
    )
      continue;
    const original =
      state.snapshot?.bookings.find((x) => x.requestId === q.booking.correctsId)
        ?.booking ??
      state.queue.find(
        (entry) =>
          entry.state === 'confirmed' &&
          entry.booking.requestId === q.booking.correctsId,
      )?.booking;
    const line = q.booking.lines.find((x) => x.articleId === articleId);
    const sign =
      q.booking.kind === 'issue'
        ? -1
        : q.booking.kind === 'correction'
          ? (original?.kind === 'issue' ? -1 : 1) *
            (line?.addInsteadOfReverse ? 1 : -1)
          : 1;
    quantity += sign * (line?.quantity ?? 0);
  }
  return Math.round(quantity * 1000) / 1000;
}

// Persistenz erfolgt vor dem Netzwerkaufruf und vor jeder Erfolgsanzeige.
export async function syncWarehouse(
  state: WarehouseLocal,
  transport: WarehouseTransport,
  save: (s: WarehouseLocal) => Promise<void>,
  maySend: () => boolean,
): Promise<WarehouseLocal> {
  const next = structuredClone(state);
  for (const q of next.queue) {
    if (!maySend()) break;
    if (!['pending', 'unknown'].includes(q.state)) continue;
    try {
      const previous =
        q.state === 'unknown'
          ? await transport.status(q.booking.requestId)
          : undefined;
      if (!maySend()) break;
      q.state = 'unknown';
      await save(next);
      if (!maySend()) break;
      const result = previous ?? (await transport.book(q.booking));
      q.result = result;
      q.state = result.status;
      q.stockIncluded = false;
      q.error = result.message ?? undefined;
      await save(next);
    } catch (error) {
      q.error =
        error instanceof Error
          ? error.message
          : 'Übertragung unklar. Status erneut prüfen.';
      await save(next);
      if (warehouseAccessStatus(error)) throw error;
      break;
    }
  }
  // Die Bestätigung bleibt erhalten, auch wenn das anschließende Lesen scheitert.
  if (maySend()) {
    try {
      Object.assign(
        next,
        withWarehouseSnapshot(
          next,
          await transport.snapshot(),
          confirmedWarehouseRequests(next),
        ),
      );
      await save(next);
    } catch (error) {
      if (warehouseAccessStatus(error)) throw error;
      /* Letzter bestätigter Stand bleibt ausdrücklich als Datenstand sichtbar. */
    }
  }
  return next;
}
